import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { SnippetHtml } from "@/components/SnippetHtml";
// Shared modular snippets — same components ship on /roadtrip.
// See /app/memory/AGENT_RULES.md: never re-implement.
import TripMap        from "@/components/TripMap";
import CheckinButton  from "@/components/CheckinButton";
import SmsSubscribers from "@/components/SmsSubscribers";

/**
 * FinderPoiDetailPage — dedicated page per POI, doubles as the SEO
 * landing target from ``/finder/poi/:id``.
 *
 *   • Sets <title>, description, canonical + basic JSON-LD.
 *   • Deep-linked from search results and from the sitemap.
 *   • Also serves as the target for the future 1.5 M-page suburb-slug
 *     rewrite (``/finder/toilet/oxley-4075/…``).
 */
export default function FinderPoiDetailPage() {
  const { id } = useParams();
  const nav    = useNavigate();
  const [poi, setPoi]   = useState(null);
  const [err, setErr]   = useState(null);
  const [addrLoading, setAddrLoading] = useState(false);
  const [trail, setTrail]       = useState([]);
  const [showSms, setShowSms]   = useState(false);

  // Shared /api/checkin/trail — same endpoint powers /roadtrip.
  const API = process.env.REACT_APP_BACKEND_URL || "";
  const loadTrail = React.useCallback(async () => {
    if (!id) return;
    try {
      const r = await fetch(
        `${API}/api/checkin/trail?source=finder`
        + `&key=${encodeURIComponent("poi:" + id)}`);
      const j = await r.json();
      setTrail(j.points || []);
    } catch { /* silent */ }
  }, [API, id]);
  useEffect(() => { loadTrail(); }, [loadTrail]);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await fetch(
          (process.env.REACT_APP_BACKEND_URL || "") + `/api/geo/poi/${id}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!dead) setPoi(j.poi || j);
      } catch (e) {
        if (!dead) setErr(e.message);
      }
    })();
    return () => { dead = true; };
  }, [id]);

  // Auto reverse-geocode when street + formatted are both missing.
  // Result is server-cached so this only ever hits Nominatim once per POI.
  useEffect(() => {
    if (!poi) return;
    const a = poi.address || {};
    if (a.formatted || a.street) return;
    let dead = false;
    setAddrLoading(true);
    (async () => {
      try {
        const r = await fetch(
          (process.env.REACT_APP_BACKEND_URL || "") +
          `/api/geo/poi/${poi.id}/reverse-geocode`,
          { method: "POST" }
        );
        if (!r.ok) return;
        const j = await r.json();
        if (!dead && j.poi) setPoi(j.poi);
      } catch (_e) { /* silent — coordinates still shown */ }
      finally { if (!dead) setAddrLoading(false); }
    })();
    return () => { dead = true; };
  }, [poi]);


  // Set page meta from the loaded POI (client-side; Googlebot executes JS).
  useEffect(() => {
    if (!poi) return;
    const a = poi.address || {};
    const suburb   = a.suburb   || "Australia";
    const postcode = a.postcode || "";
    const typeH    = (poi.type || "place").replace(/_/g, " ");
    const title    = `${poi.name} · ${typeH} in ${suburb} ${postcode} — HustleFinder`;
    document.title = title;

    const desc = `${poi.name} is a ${typeH} located in ${suburb}` +
                  (postcode ? ` ${postcode}` : "") +
                  `, Australia. Free, up-to-date location details on HustleFinder.`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    m.setAttribute("content", desc);

    // canonical
    const canon = `https://hustlefind.au/finder/poi/${poi.id}`;
    let l = document.querySelector('link[rel="canonical"]');
    if (!l) {
      l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      document.head.appendChild(l);
    }
    l.setAttribute("href", canon);

    // JSON-LD Place schema
    const existing = document.getElementById("poi-jsonld");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id   = "poi-jsonld";
    const [lng, lat] = poi.loc?.coordinates || [];
    const streetLine = [a.house_number, a.street].filter(Boolean).join(" ");
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type":    "Place",
      "name":     poi.name,
      "address":  {
        "@type":            "PostalAddress",
        ...(streetLine ? { "streetAddress": streetLine } : {}),
        "addressLocality":  suburb,
        "postalCode":       postcode,
        "addressRegion":    a.state || "",
        "addressCountry":   "AU",
      },
      ...(lat && lng ? { "geo": { "@type": "GeoCoordinates", latitude: lat, longitude: lng } } : {}),
      "url": canon,
    });
    document.head.appendChild(script);
  }, [poi]);

  if (err) {
    return (
      <div data-testid="poi-error"
           className="max-w-xl mx-auto p-6 text-slate-700">
        Couldn&apos;t load this place ({err}).{" "}
        <button onClick={() => nav("/finder")}
                className="text-emerald-700 underline">Back to search</button>
      </div>
    );
  }
  if (!poi) {
    return (
      <div data-testid="poi-loading"
           className="max-w-xl mx-auto p-6 text-slate-500">Loading…</div>
    );
  }

  const a = poi.address || {};
  const [lng, lat] = poi.loc?.coordinates || [];
  const typeH    = (poi.type || "").replace(/_/g, " ");
  const subH     = poi.subtype && poi.subtype !== poi.type
      ? poi.subtype.replace(/_/g, " ") : null;
  const streetLine = [a.house_number, a.street].filter(Boolean).join(" ");

  // Build the best human-readable destination string for map apps.
  // Precedence: full formatted address > street + suburb + state + postcode >
  // POI name + suburb (never just raw lat/lng — that gives Maps no anchor).
  const suburbLine = [a.suburb, a.state, a.postcode]
    .filter(Boolean).join(" ");
  const destText = (
    a.formatted ||
    [streetLine, suburbLine].filter(Boolean).join(", ") ||
    [poi.name, suburbLine].filter(Boolean).join(", ") ||
    (lat && lng ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : "")
  );

  // Proper turn-by-turn directions URLs. Google's "/maps/dir/?api=1" and
  // Apple's "dirflg=d" both open the routing view directly instead of
  // just dropping a pin, and passing the name via ``q=`` gives the map
  // app a label so users know exactly what they're navigating to.
  const gmaps = destText
      ? `https://www.google.com/maps/dir/?api=1`
        + `&destination=${encodeURIComponent(destText)}`
        + `&travelmode=driving`
      : null;
  const apple = destText
      ? `https://maps.apple.com/?`
        + `daddr=${encodeURIComponent(destText)}`
        + `&dirflg=d`
        + (poi.name ? `&q=${encodeURIComponent(poi.name)}` : "")
      : null;
  // Human-readable single-line address for the top display, always non-empty.
  const displayAddress = (
    a.formatted ||
    [streetLine, suburbLine].filter(Boolean).join(", ") ||
    (suburbLine ? `near ${suburbLine}` : "") ||
    "Location details being resolved…"
  );

  return (
    <div data-testid="poi-detail-page"
         className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/finder"
              data-testid="poi-back-link"
              className="text-xs text-emerald-700 hover:underline">
          ← back to search
        </Link>
        <h1 data-testid="poi-name"
            className="text-3xl font-bold text-slate-900 mt-2 mb-1">
          {poi.name}
        </h1>
        <div className="text-sm text-slate-500 mb-6"
             data-testid="poi-subheader">
          {typeH}{subH ? ` · ${subH}` : ""}
          {streetLine ? ` · ${streetLine}` : ""}
          {a.suburb ? ` · ${a.suburb}` : ""}
          {a.state ? ` ${a.state}` : ""}
          {a.postcode ? ` ${a.postcode}` : ""}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 mb-5">
          <div className="mb-4 pb-4 border-b border-slate-100"
               data-testid="poi-address-wrap">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
              Address
            </div>
            {(streetLine || a.formatted) ? (
              <div className="text-sm text-slate-800 leading-relaxed"
                   data-testid="poi-address-inline">
                {streetLine && (
                  <div className="font-medium text-slate-900">{streetLine}</div>
                )}
                {suburbLine && (
                  <div className="text-slate-700">
                    {[a.suburb, a.state, a.postcode].filter(Boolean).join(" ")}
                  </div>
                )}
                {a.formatted && a.formatted !== streetLine && (
                  <div className="text-xs text-slate-500 mt-1">
                    {a.formatted}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-700"
                   data-testid="poi-address-fallback">
                {displayAddress}
                {addrLoading && (
                  <span className="ml-2 text-slate-400 text-xs">
                    · resolving street…
                  </span>
                )}
              </div>
            )}
          </div>

          {/*
            Live "Open now · closes 8pm" badge — only renders if the
            server has meta.opening_hours for this POI, otherwise
            the SnippetHtml component hides itself.
          */}
          <div className="mb-3" data-testid="poi-hours-wrap">
            <SnippetHtml
              scope="global"
              name="open_hours"
              params={{ poi_id: poi.id }}
              className="hp-oh-wrap"
            />
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                Category
              </div>
              <div className="text-slate-800 capitalize">{typeH}</div>
            </div>
            {subH && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                  Sub-category
                </div>
                <div className="text-slate-800 capitalize">{subH}</div>
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                Suburb
              </div>
              <div className="text-slate-800">{a.suburb || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                Postcode
              </div>
              <div className="text-slate-800">{a.postcode || "—"}</div>
            </div>
            {lat && lng && (
              <div className="col-span-2">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                  Coordinates
                </div>
                <div className="text-slate-800 font-mono text-xs">
                  {lat.toFixed(6)}, {lng.toFixed(6)}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {gmaps && (
            <a href={gmaps} target="_blank" rel="noreferrer"
               data-testid="poi-directions-google"
               className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm">
              Directions (Google Maps)
            </a>
          )}
          {apple && (
            <a href={apple} target="_blank" rel="noreferrer"
               data-testid="poi-directions-apple"
               className="px-4 py-2 rounded-lg bg-slate-800 text-white text-sm">
              Directions (Apple Maps)
            </a>
          )}
        </div>

        {/* Check-in + map + SMS group — shared modular snippet used
            identically on /roadtrip. Visitors can "text my group I'm
            here" from any POI page. Trail below shows every past
            check-in for this listing so the owner can see foot-traffic
            at a glance. */}
        {typeof poi?.lat === "number" && typeof poi?.lng === "number" && (
          <div className="mt-6 pt-6 border-t border-slate-200 space-y-4"
                data-testid="poi-checkin-section">
            <TripMap
              mode="trail"
              stops={[{ lat: poi.lat, lng: poi.lng,
                         label: poi.name || "This listing",
                         kind: "stop",
                         subtitle: poi.address || "" }]}
              trail={trail}
              height={280}
              testid="poi-tripmap"
            />
            <div className="flex flex-wrap items-center gap-3">
              <CheckinButton
                source="finder"
                targetKey={`poi:${id}`}
                label="I'm here — text my group"
                onSuccess={loadTrail}
                testid="poi-checkin"
              />
              <button
                type="button"
                data-testid="poi-manage-sms"
                onClick={() => setShowSms((s) => !s)}
                className="text-slate-500 hover:text-slate-700 text-sm underline underline-offset-4"
              >
                {showSms ? "Hide SMS group" : "Manage SMS group…"}
              </button>
            </div>
            {showSms && (
              <SmsSubscribers source="finder"
                              targetKey={`poi:${id}`}
                              testid="poi-sms"/>
            )}
          </div>
        )}

        <div className="mt-8 text-xs text-slate-400">
          Source: OpenStreetMap contributors (ODbL) · data.gov.au (CC-BY 4.0).{" "}
          <Link to="/credits" className="underline">Full credits</Link>.
        </div>
      </div>
    </div>
  );
}
